/** @jsxImportSource @emotion/react */
import { type DragEvent, useRef, useState } from 'react';

import { UploadIcon } from '@kaiizen/kai-shiki/react';

import {
  SCAN_VIDEO_ACCEPT,
  SCAN_VIDEO_MAX_FILES,
} from '@kaiizen/kai-pro-api';
import {
  DROPZONE_BROWSE_STYLE,
  DROPZONE_ICON_STYLE,
  DROPZONE_STYLE,
  DROPZONE_SUBTITLE_STYLE,
  DROPZONE_TITLE_STYLE,
  dropzoneDraggingStyle,
  dropzoneFullStyle,
} from './aiScanVideoUploadModalStyles';

type AIScanVideoUploadDropzoneProps = {
  disabled?: boolean;
  fileCount: number;
  onSelectFiles: (files: FileList | File[]) => void;
};

export const AIScanVideoUploadDropzone = ({
  disabled = false,
  fileCount,
  onSelectFiles,
}: AIScanVideoUploadDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const isFull = fileCount >= SCAN_VIDEO_MAX_FILES;
  const inactive = disabled || isFull;

  const openPicker = () => {
    if (inactive) return;
    inputRef.current?.click();
  };

  const handleDragEnter = (event: DragEvent) => {
    event.preventDefault();
    if (!inactive) setDragging(true);
  };

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    if (!inactive) setDragging(true);
  };

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (inactive) return;
    if (event.dataTransfer.files?.length) onSelectFiles(event.dataTransfer.files);
  };

  return (
    <>
      <div
        css={[DROPZONE_STYLE, dragging && !inactive && dropzoneDraggingStyle, isFull && dropzoneFullStyle]}
        role={'button'}
        tabIndex={inactive ? -1 : 0}
        aria-label={'Choose a video file'}
        aria-disabled={inactive}
        onClick={openPicker}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPicker();
          }
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <span css={DROPZONE_ICON_STYLE}>
          <UploadIcon size={38} color={'currentColor'} aria-hidden={'true'} />
        </span>
        <div css={DROPZONE_TITLE_STYLE}>Drag and drop your video</div>
        <div css={DROPZONE_SUBTITLE_STYLE}>
          or <span css={DROPZONE_BROWSE_STYLE}>browse files</span> to upload
        </div>
      </div>
      <input
        ref={inputRef}
        type={'file'}
        accept={SCAN_VIDEO_ACCEPT}
        hidden
        disabled={inactive}
        onChange={event => {
          if (event.target.files?.length) onSelectFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </>
  );
};
